import { useState } from "react";
import { Button, CircularProgress, DialogActions, DialogContent, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { pt } from "date-fns/locale";
import { format } from "date-fns";
import { financeApi } from "../../../api/config";
import { getStore } from "../../../store/store";
import { showResponseDialog } from "../bankTransactionsActions";

type DateFieldDefinition = {
    key: string;
    label: string;
};

type DateActionDialogProps = {
    onClose: () => void;
    endpoint: string;
    title: string;
    description: string;
    dateFields: Array<DateFieldDefinition>;
};

export const DateActionDialog = ({ onClose, endpoint, title, description, dateFields }: DateActionDialogProps) => {
    const [dateValues, setDateValues] = useState<Record<string, Date | null>>({});
    const [isLoading, setIsLoading] = useState(false);
    const { notifications } = getStore();

    const handleDateChange = (key: string, value: Date | null) => {
        setDateValues((previous) => ({ ...previous, [key]: value }));
    };

    const handleSubmit = async () => {
        setIsLoading(true);

        try {
            const requestBody: Record<string, string> = {};
            for (const field of dateFields) {
                const dateValue = dateValues[field.key];
                if (dateValue) requestBody[field.key] = format(dateValue, "yyyy-MM-dd");
            }

            const response = await financeApi.post(`/ai-finance-endpoint/${endpoint}`, requestBody);

            showResponseDialog(title, response.data);
        } catch (error: any) {
            notifications.setNotification({
                isOpen: true,
                message: error?.response?.data?.error || "Ocorreu um erro ao executar a ação",
                severity: "error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    {description}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pt}>
                    {dateFields.map((field) => (
                        <DatePicker
                            key={field.key}
                            label={field.label}
                            value={dateValues[field.key] ?? null}
                            onChange={(value) => handleDateChange(field.key, value)}
                            format="dd/MM/yyyy"
                            sx={{ width: "100%" }}
                        />
                    ))}
                </LocalizationProvider>
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    Cancelar
                </Button>
                <Button onClick={handleSubmit} disabled={isLoading}>
                    {isLoading ? <CircularProgress color="inherit" size={24} /> : "Executar"}
                </Button>
            </DialogActions>
        </>
    );
};
